import { z } from 'zod';

// ─── Per-cluster unit-address scheme ────────────────────────────────────────
// Number is always required (no flag). Street and Block are independently
// toggleable by the platform admin. See
// docs/superpowers/specs/2026-06-27-configurable-unit-address-scheme-design.md.
export const addressScheme = z.object({
  addr_street_enabled: z.boolean(),
  addr_block_enabled: z.boolean(),
});
export type AddressScheme = z.infer<typeof addressScheme>;

export const DEFAULT_ADDRESS_SCHEME: AddressScheme = {
  addr_street_enabled: false,
  addr_block_enabled: true,
};

// Raw, possibly-untrimmed parts as they arrive from a form/request. Street and
// block are optional in the wire shape; presence is enforced per the scheme.
export interface AddressPartsInput {
  street?: string | null;
  block?: string | null;
  number?: string | null;
}

export interface AddressParts {
  street: string;
  block: string;
  number: string;
}

export type CheckAddressPartsResult =
  | { ok: true; value: AddressParts }
  | { ok: false; message: string };

/**
 * Validate + normalize address parts against a cluster's scheme.
 *
 * Rules:
 *   - Number  — always required, non-empty.
 *   - Block   — required & non-empty when enabled; must be empty when disabled.
 *   - Street  — optional even when enabled (may be blank); must be empty when
 *               disabled.
 *
 * Disabled/blank parts normalize to '' so the `(cluster, street, block, number)`
 * unique index stays well-defined. Shared by the API (throws AppError on the
 * `ok:false` branch) and the admin UI (gates the submit button).
 */
export function checkAddressParts(
  scheme: AddressScheme,
  input: AddressPartsInput,
): CheckAddressPartsResult {
  const street = (input.street ?? '').trim();
  const block = (input.block ?? '').trim();
  const number = (input.number ?? '').trim();

  if (!number) return { ok: false, message: 'Number is required.' };

  if (scheme.addr_block_enabled) {
    if (!block) return { ok: false, message: 'Block is required for this cluster.' };
  } else if (block) {
    return { ok: false, message: 'This cluster does not use a Block.' };
  }

  if (!scheme.addr_street_enabled && street) {
    return { ok: false, message: 'This cluster does not use a Street number.' };
  }

  return {
    ok: true,
    value: {
      street: scheme.addr_street_enabled ? street : '',
      block: scheme.addr_block_enabled ? block : '',
      number,
    },
  };
}

/** Space-join the non-empty parts (street → block → number) into a label. */
export function deriveUnitLabel(parts: AddressPartsInput): string {
  return [parts.street, parts.block, parts.number]
    .map((p) => (p ?? '').trim())
    .filter(Boolean)
    .join(' ');
}
